// services.js
//
// Structured page content for the Services_Page. Copy is drawn from the
// ThinkSpatial Capability Statement so the website and the capability document
// tell the same story, and lives here rather than in component templates so
// editorial changes need no redesign (Requirement 11.2).
//
// `ServicesPage.vue` imports this module.
//
// NOTE ON TAXONOMY: this list describes the full capability set from the
// capability statement. It is deliberately INDEPENDENT of the
// `SERVICE_CATEGORIES` enumeration in services/serviceCategories.js, which is
// the narrower set used to tag and filter projects (and which mirrors the
// ProjectTypes table in the database). Expanding the project-tagging taxonomy
// would require a database migration, so the two are kept separate for now.

import heroImage from 'assets/img/parallax/aerial2.jpg'

/**
 * A single capability area.
 *
 * @typedef {Object} ServiceArea
 * @property {string} id       - stable slug, used as the in-page anchor id
 * @property {string} title    - display name
 * @property {string} summary  - one-line summary used in the jump-links grid
 * @property {string[]} body   - descriptive paragraphs
 * @property {string[]} [points] - optional bulleted detail
 */

/**
 * The Services_Page content model.
 *
 * @typedef {Object} ServicesContent
 * @property {Object} intro  - page heading / lead copy
 * @property {ServiceArea[]} areas - the capability areas, in display order
 * @property {Object} closingCta - CTA to Contact
 * @property {import('./seo').PageSeo} seo - per-page SEO metadata
 */

export const services = {
  intro: {
    heading: 'Our services',
    lead:
      'ThinkSpatial integrates traditional surveying with advanced data management, real-time monitoring, and web-based collaboration tools. From subsurface utilities to 3D models, we provide the insights you need to build smarter and faster.',
    image: {
      src: heroImage,
      alt: 'Aerial survey imagery of an infrastructure project captured by ThinkSpatial'
    }
  },

  areas: [
    {
      id: 'survey-spatial-data-management',
      title: 'Survey & Spatial Data Management',
      summary:
        'Project Survey Managers and Spatial Data Managers embedded on large-scale construction projects.',
      body: [
        'Providing Project Survey Managers and Spatial Data Managers on large-scale construction projects has proven crucial to ensuring a project runs smoothly and efficiently.',
        'Our managers coordinate all project survey requirements with delivery teams, compile existing and incoming spatial data, request current design data from design teams, and convert datasets to suitable formats. They work with designers to identify improvements, streamline the flow of spatial data, and process and merge feature survey information to maintain current master datasets — through to the delivery of final as-built datasets for compliance and completion.'
      ]
    },
    {
      id: 'construction-survey',
      title: 'Construction Survey',
      summary:
        'Set-out backed by design-team collaboration and crews that scale to the project.',
      body: [
        'ThinkSpatial streamlines set-out by collaborating with designers and engineers to seamlessly convert design data for survey use. We proactively engage with design teams to clarify data requirements, minimising errors and ensuring smooth, efficient set-out operations.',
        'Our survey teams are bolstered by comprehensive back-office expertise, ensuring seamless support for all onsite activities, and have access to a wide range of state-of-the-art equipment.'
      ],
      points: [
        'Small, part-time works',
        'Medium-sized projects requiring part- and full-time survey resources over several months',
        'Large-scale infrastructure projects requiring 6–15 surveyors full-time, including night shift and weekend shifts, scaling up for occupations'
      ]
    },
    {
      id: 'underground-services',
      title: 'Underground Services',
      summary:
        'AS5488-compliant subsurface utility capture, QA, and presentation during early works.',
      body: [
        'ThinkSpatial has developed a comprehensive set of procedures for the survey and presentation of underground utilities infrastructure over the course of many construction projects, generally during the “early works” phase. Teaming up with utility locating teams, we provide survey and data capture in the field with a rigorous list of QA procedures to ensure data quality, completeness, and accuracy.',
        'We are leaders in spatial data management, and this is never more important than when recording the location and details of underground utilities to facilitate engineering design and construction.',
        'Data captured during locating activities is processed daily and fed through automated procedures to ensure a complete master dataset is maintained throughout the field campaign. This data is compliant with AS5488, maintained in a Geographic Information System, and exported to CAD formats as required.'
      ]
    },
    {
      id: 'feature-surveys',
      title: 'Feature Surveys',
      summary:
        'Survey for design, planning, assessment, and analysis, to your drafting standards.',
      body: [
        'Feature surveys are undertaken for design, planning, assessment, and analysis purposes, including the production of digital and hardcopy plans and maps for a variety of engineering purposes. Utility services are maintained and updated in CAD files, constantly revised as new information is collected through investigation activities.',
        'ThinkSpatial has complete flexibility to set up and adhere to project drafting standards. Feature codes, layer and drawing naming conventions are all established quickly at project commencement. We can establish a complete workflow to suit any project, ensuring all features — including utility services — are surveyed and delivered in accordance with the Underground Utilities Survey Guideline and the Modelling and Drafting Management Plan.'
      ]
    },
    {
      id: 'cad-drafting-modelling',
      title: 'CAD – Drafting & Modelling',
      summary:
        'Drafting, as-built drawings, and CAD/BIM models across all common packages.',
      body: [
        'Survey and design CAD information can be prepared very quickly to give engineers what they need for planning meetings, temporary works, design clash detection, clearance calculations, and works programming including 4D visualisations. We also specialise in preparing as-built drawings for project completion, taking original design plans and revising them using survey, redline, and other information.',
        'ThinkSpatial has extensive modelling capabilities, creating visually accurate CAD and BIM models from survey data — such as point clouds from laser scanning, or underground services modelled to their correct diameters and locations underground. Deliverables can be presented in AutoCAD, MicroStation, Navisworks, and Revit, as well as most other common CAD/BIM packages.'
      ]
    },
    {
      id: 'laser-scanning',
      title: 'Laser Scanning',
      summary:
        'Terrestrial, mobile, and SLAM scanning delivered as point clouds, plans, or BIM models.',
      body: [
        'ThinkSpatial has significant experience in terrestrial laser scanning (TLS) and mobile laser scanning (MLS), combining methodologies where appropriate to produce the required level of detail for the best value.',
        'As well as being an excellent tool for capturing large amounts of complex data — saving time and money — laser scanning increases survey safety. The resulting point clouds are modelled and presented as anything from 2D floor plans to highly detailed BIM models.'
      ],
      points: [
        'Volumes and hardstand conformance',
        'Feature surveys and detailed as-builts',
        'Haul route investigations',
        'Bridge surveys and deformation monitoring',
        'Building floor plans'
      ]
    },
    {
      id: 'monitoring-surveys',
      title: 'Monitoring Surveys',
      summary:
        'Manual and automated monitoring with real-time analysis and SMS/email alerting.',
      body: [
        'Monitoring surveys track the settlement of ground and the movement of structures before, during, and after construction activity. Knowing whether construction is causing movement to the built environment is crucial to risk management and can provide early warning to prevent or limit damage to structures and risk to lives.',
        'Our automated monitoring solution uses survey prisms and sensors attached to structures such as tunnels, buildings, bridges, piles, retaining walls, and railways. These continually feed real-time data back to a server where it is analysed, compiled into graphs and reports, and can trigger real-time alerts via email and SMS.',
        'Where monitoring is mandatory for compliance, automated techniques are generally preferred: they respond immediately to movement, take far more detailed and frequent measurements, and can cover areas that would usually prohibit entry by people. Automated methods are often more economically viable and generally safer than manual monitoring.'
      ]
    },
    {
      id: 'uav-aerial-surveying',
      title: 'UAV Aerial Surveying',
      summary:
        'Drone-based aerial mapping, volumes, and inspection across hard-to-reach sites.',
      body: [
        'ThinkSpatial provides UAV aerial surveying services for mapping, volume calculation, and inspection — particularly valuable across large or hard-to-access sites where traditional capture would be slower or less safe.',
        'Aerial capture is supported by our ground control expertise, ensuring UAV deliverables are accurately positioned and fit for engineering use.'
      ]
    },
    {
      id: 'other-surveys',
      title: 'Other Surveys',
      summary:
        'A long history across water, environmental, and asset survey disciplines.',
      body: [
        'ThinkSpatial has a long history of performing many other types of survey beyond construction work.'
      ],
      points: [
        'Drainage and flood modelling',
        'Levees and irrigation channels',
        'Asset condition and mapping',
        'Wetlands and waterways',
        'Coastal monitoring',
        'Dam wall monitoring',
        'Groundwater monitoring',
        'Ground control for large aerial surveys'
      ]
    }
  ],

  closingCta: {
    heading: 'Need a specific capability?',
    body:
      'Tell us about your project and we will help you scope the right survey and spatial approach.',
    cta: { label: 'Contact us', to: '/contact' }
  },

  /** @type {import('./seo').PageSeo} */
  seo: {
    title: 'Services — ThinkSpatial',
    description:
      'Survey and spatial data management, construction survey, underground services, laser scanning, CAD and BIM modelling, monitoring, and UAV surveying across Australia.'
  }
}

export default services
